import React, { useState, useRef, useEffect } from "react";

import BcSepIcon from "../../asset/images/about/breadcrumb-separator.svg";

// Icons for advantages
import PowerIcon    from "../../asset/images/products/precicon-r3/icon-power.svg";
import CheckIcon    from "../../asset/images/products/precicon-r3/icon-check.svg";
import MonitorIcon  from "../../asset/images/products/precicon-r3/icon-monitor.svg";
import HardhatIcon  from "../../asset/images/products/precicon-r3/icon-hardhat.svg";
import RapperIcon   from "../../asset/images/products/precicon-r3/icon-rapper.svg";
import FactoryIcon  from "../../asset/images/products/precicon-r3/icon-factory.svg";
import LeafIcon     from "../../asset/images/products/precicon-r3/icon-leaf.svg";

// HMI image for spec table
import HmiImg from "../../asset/images/products/precicon-r3/hmi.png";

// Product image
import ProductImg from "../../asset/images/products/precicon-r3/product.webp";


const ADVANTAGES = [
  { icon: PowerIcon,   text: "Advanced thyristor-based controller with 3-phase input for superior stability and efficiency." },
  { icon: CheckIcon,   text: "Reliable performance in challenging environments with IP-54-rated protection." },
  { icon: MonitorIcon, text: "Seamless integration via CAN Bus communication and DCSIM Gateway for remote monitoring." },
  { icon: HardhatIcon, text: "Galvanic isolation and in-built rapping controls optimize dust removal and efficiency." },
  { icon: RapperIcon,  text: "Reduced risk of voltage dips and enhanced dust separation under fluctuating loads" },
];

const END_APPLICATIONS = [
  { icon: FactoryIcon, text: "Industries: Cement, steel, power, and chemicals." },
  { icon: LeafIcon,    text: "Ideal for large-scale ESPs requiring high precision & environmental compliance." },
];

const SPECS = [
  { label: "Controller",                         value: "Precicon R3",       isImage: false },
  { label: "HMI",                                value: HmiImg,              isImage: true  },
  { label: "No Of Controller Operated By 1 HMI", value: "30 Nos",            isImage: false },
  { label: "HMI IP Protection",                  value: "IP 54",             isImage: false },
  { label: "Operating Technology",               value: "Thyristor Based",   isImage: false },
  { label: "Input Phase",                        value: "3 Phase",           isImage: false },
  { label: "Output Phase",                       value: "3 Phase",           isImage: false },
  { label: "Line Frequency",                     value: "50/60 Hz",          isImage: false },
  { label: "Input Voltage",                      value: "380-690V (L-L)",    isImage: false },
  { label: "Output Voltage",                     value: "As Per Rated TR",   isImage: false },
  { label: "Controller Input Supply",            value: "110 VAC, -10% +10%, 50/60 Hz", isImage: false },
  { label: "No. Input Signal",                   value: "10",                isImage: false },
  { label: "No. Output Signal",                  value: "4",                 isImage: false },
  { label: "Output Signal Protection",           value: "Galvanic Isolation", isImage: false },
  { label: "Output Resistance",                  value: "750 Ohms (Max)",    isImage: false },
  { label: "Communication Protocol",             value: "CAN Bus",           isImage: false },
  { label: "In Built No. Of Rapper Controlling", value: "4",                 isImage: false },
  { label: "Remote Monitoring",                  value: "Data Can Be Monitored On DCS With The Help Of Gateway DCSIM", isImage: false },
  { label: "Ambient Temp",                       value: "50°C",              isImage: false },
  { label: "Humidity",                           value: "95% Max, Non-Condensing", isImage: false },
  { label: "Approvals",                          value: "CE, CSA",           isImage: false },
];

const FAQS = [
  {
    q: "What makes Precicon R3 suitable for large-scale Electrostatic Precipitators (ESPs)?",
    a: "Precicon R3 features a 3-phase input, providing superior stability and efficiency compared to single-phase systems. This ensures precise power delivery, making it ideal for large-scale ESPs across industries like cement, steel, power, and chemicals.",
  },
  {
    q: "How does Precicon R3 ensure reliable performance in challenging environments?",
    a: "Precicon R3 is built with IP-54-rated protection, enabling it to perform reliably in demanding industrial conditions. Additionally, its galvanic isolation and in-built rapping controls optimize dust removal and maintain consistent performance.",
  },
  {
    q: "What integration and monitoring capabilities does Precicon R3 offer?",
    a: "Precicon R3 supports CAN Bus communication for seamless system integration and remote monitoring via the DCSIM Gateway. This ensures continuous operation with minimal downtime and enables proactive maintenance.",
  },
  {
    q: "How does Precicon R3 contribute to operational efficiency and cost savings?",
    a: "By minimizing energy losses, reducing voltage dips, and enhancing dust separation efficiency under fluctuating loads, Precicon R3 helps industries lower operational costs while maintaining high efficiency.",
  },
  {
    q: "How does Precicon R3 support environmental compliance?",
    a: "Precicon R3 is designed to maximize dust collection and optimize ESP performance, ensuring industries meet stringent environmental standards with ease and reliability.",
  },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .pcr3-wrap { font-family: 'Poppins', sans-serif; color: #202020; background: #fff; }

  /* ══════════════════════════════════════════
     1. BREADCRUMB
  ══════════════════════════════════════════ */
  .pcr3-breadcrumb { padding: 60px 0 0 0; }
  .pcr3-breadcrumb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .pcr3-bc-item {
    font-size: 16px;
    font-weight: 500;
    color: #202020;
    text-decoration: none;
    transition: color 0.15s;
  }
  .pcr3-bc-item:hover { color: #EB2019; }
  .pcr3-bc-item.pcr3-active { color: #202020; }
  .pcr3-bc-sep {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin: 0 8px;
    flex-shrink: 0;
  }

  /* ══════════════════════════════════════════
     SHARED INNER
  ══════════════════════════════════════════ */
  .pcr3-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }

  /* ══════════════════════════════════════════
     2. INTRO — image left + title + paras right
  ══════════════════════════════════════════ */
  .pcr3-intro {
    padding: 60px 0 120px 0;
  }
  .pcr3-intro-grid {
    display: flex;
    gap: 75px;
    align-items: center;
  }
  .pcr3-intro-img-wrap {
    flex: 0 0 400px;
    border-radius: 10px;
    overflow: hidden;
  }
  .pcr3-intro-img {
    width: 100%;
    height: 550px;
    object-fit: cover;
    display: block;
  }
  .pcr3-intro-right {
    flex: 1;
  }
  .pcr3-intro-title {
    font-size: 32px;
    font-weight: 600;
    color: #202020;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .pcr3-para {
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }
  .pcr3-para + .pcr3-para { margin-top: 18px; }

  /* ══════════════════════════════════════════
     SHARED DIVIDER
  ══════════════════════════════════════════ */
  .pcr3-divider {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }
  .pcr3-divider hr {
    border: none;
    border-top: 1px solid #D4D4D4;
  }

  /* ══════════════════════════════════════════
     3. ADVANTAGES & APPLICATIONS
  ══════════════════════════════════════════ */
  .pcr3-adv-section {
    padding: 0px 0 60px 0;
  }
  .pcr3-section-red-title {
    font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    text-align: center;
    margin-bottom: 0px;
  }
  .pcr3-section-red-title1 {
    font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    text-align: center;
    margin-bottom: 60px;
  }
  .pcr3-sub-heading {
    font-size: 32px;
    font-weight: 600;
    color: #202020;
    text-align: center;
    margin-bottom: 34px;
    padding-top: 60px;
  }

  /* Icon cards grid */
  .pcr3-icon-row {
    display: flex;
    justify-content: center;
    gap: 31px;
    margin-bottom: 60px;
  }
  .pcr3-icon-row:last-child {
    display: flex;
    justify-content: center;
    gap: 31px;
    margin-bottom: 0px;
  }
  .pcr3-icon-card {
    flex: 0 0 445px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    text-align: center;
  }
  .pcr3-icon-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .pcr3-icon-circle svg {
    width: 84px;
    height: 84px;
    filter: brightness(0) invert(1);
    display: block;
  }
  .pcr3-icon-text {
    font-size: 20px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }

  /* ══════════════════════════════════════════
     4. END APPLICATIONS
  ══════════════════════════════════════════ */
  .pcr3-app-section {
    padding: 0 0 80px 0;
  }
  .pcr3-contact-para1 {
    font-size: 20px;
    font-weight: 400;
    color: #202020;
    text-align: center;
    line-height: 160%;
    margin-bottom:24px;
  }
    .pcr3-contact-para {
    font-size: 20px;
    font-weight: 500;
    color: #202020;
    text-align: center;
    line-height: 160%;
  }
  .pcr3-contact-para a {
    color: #EB2019;
    text-decoration: none;
  }
  .pcr3-contact-para a:hover { text-decoration: underline; }

  /* ══════════════════════════════════════════
     5. PRODUCT SPECIFICATIONS TABLE
  ══════════════════════════════════════════ */
  .pcr3-spec-section {
    padding: 80px 0 0px 0;
  }
  .pcr3-spec-table-wrap {
    border: 1px solid #D9D9D9;
    border-radius: 0;
    overflow: hidden;
    max-width: 1158px;
    margin: 0 auto;
  }
  .pcr3-spec-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 16px;
    color: #202020;
  }
  .pcr3-spec-table thead tr { background: #F6F6F6; }
  .pcr3-spec-table thead th {
    padding: 30px 24px;
    font-weight: 600;
    text-align: center;
    line-height: 120%;
    border-bottom: 1px solid #B8B8B8;
    border-right: 1px solid #B8B8B8;
  }
  .pcr3-spec-table thead th:last-child { border-right: none; }
  .pcr3-spec-table tbody tr { border-bottom: 1px solid #D9D9D9; }
  .pcr3-spec-table tbody tr:last-child { border-bottom: none; }
  .pcr3-spec-table tbody tr:nth-child(even) { background: #F6F6F6; }
  .pcr3-spec-table tbody td {
    padding: 30px 24px;
    text-align: center;
    border-right: 1px solid #B8B8B8;
    vertical-align: middle;
    font-weight: 400;
    line-height: 120%;
  }
  .pcr3-spec-table tbody td:last-child { border-right: none; }
  .pcr3-spec-hmi-img {
    width: 90px;
    height: 60px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  /* ══════════════════════════════════════════
     6. FAQ SECTION
  ══════════════════════════════════════════ */
  .pcr3-faq-section {
    padding: 120px 0 120px 0;
  }
  .pcr3-faq-grid {
    display: flex;
    gap: 154px;
    align-items: flex-start;
  }
  .pcr3-faq-title {
    flex: 0 0 391px;
    font-size: 42px;
    font-weight: 600;
    color: #202020;
    line-height: 140%;
  }
  .pcr3-faq-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* FAQ item wrapper */
  .pcr3-faq-item {
    border-radius: 5px;
    overflow: hidden;
  }
  .pcr3-faq-item-closed {
    background: #F6F6F6;
  }
  .pcr3-faq-item-open {
    background: #F6F6F6;
  }

  /* Question button */
  .pcr3-faq-question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 34px 20px 30px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }
  .pcr3-faq-q-text-open {
    font-size: 16px;
    font-weight: 500;
    color: #EB2019;
    line-height: 120%;
    flex: 1;
  }
  .pcr3-faq-q-text-closed {
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 150%;
    flex: 1;
  }
  .pcr3-faq-chevron {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ── Smooth accordion answer panel ── */
  .pcr3-faq-body {
    overflow: hidden;
    transition: max-height 0.4s ease, opacity 0.4s ease;
    opacity: 0;
    max-height: 0;
  }
  .pcr3-faq-body.pcr3-faq-body-open {
    opacity: 1;
  }
  .pcr3-faq-answer {
    padding: 0 48px 24px 30px;
    font-size: 14px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }

  /* ── Bottom contact para ── */
  .pcr3-bottom-contact {
    padding: 44px 0 80px 0;
    text-align: center;
  }
  .pcr3-bottom-contact p {
    font-size: 20px;
    font-weight: 500;
    color: #202020;
    line-height: 160%;
  }
  .pcr3-bottom-contact a {
    color: #EB2019;
    text-decoration: none;
  }
  .pcr3-bottom-contact a:hover { text-decoration: underline; }

  /* ══════════════════════════════════════════
     RESPONSIVE — TABLET 768px
  ══════════════════════════════════════════ */
  @media (max-width: 768px) {
    .pcr3-breadcrumb        { padding: 28px 0 0 0; }
    .pcr3-breadcrumb-inner  { padding: 0 32px; }
    .pcr3-inner             { padding: 0 32px; }
    .pcr3-divider           { padding: 0 32px; }

    .pcr3-intro             { padding: 40px 0 40px 0; }
    .pcr3-intro-grid        { flex-direction: column; gap: 24px; }
    .pcr3-icon-circle img   { width: 60px; height: 60px; }
    .pcr3-icon-circle       { width: 54px; height: 54px; }
    .pcr3-icon-text         { font-size: 16px; }
    .pcr3-intro-img-wrap    { flex: none; width: 100%; }
    .pcr3-intro-img         { height: 280px; }
    .pcr3-intro-title       { font-size: 22px; }
    .pcr3-para              { font-size: 14px; }

    .pcr3-adv-section       { padding: 0px 0 40px 0; }
    .pcr3-section-red-title { font-size: 22px; margin-bottom: 0px; }
    .pcr3-sub-heading       { font-size: 20px; margin-bottom: 30px; padding-top: 30px; }
    .pcr3-icon-row          { flex-wrap: wrap; gap: 24px; margin-bottom: 30px; }
    .pcr3-app-section       { padding: 0 0 40px 0; }
    .pcr3-icon-card         { flex: 0 0 210px; }
    .pcr3-contact-para      { font-size: 16px; text-align: left; }
    .pcr3-contact-para1      { font-size: 16px; text-align: left; }
    .pcr3-section-red-title1 { font-size: 22px; margin-bottom: 24px; }

    .pcr3-spec-section      { padding: 40px 0 40px 0; }
    .pcr3-spec-table        { font-size: 14px; }
    .pcr3-spec-table thead th { padding: 14px 12px; }
    .pcr3-spec-table tbody td { padding: 14px 12px; }

    .pcr3-faq-section       { padding: 0px 0 40px 0; }
    .pcr3-faq-grid          { flex-direction: column; gap: 24px; }
    .pcr3-faq-title         { flex: none; font-size: 22px; margin: 0 auto; }
    .pcr3-faq-q-text-open   { font-size: 14px; }
    .pcr3-faq-list          { gap: 16px; }
    .pcr3-faq-question      { padding: 20px 34px 16px 30px; }
    .pcr3-faq-answer        { padding: 0 44px 24px 30px; }
    .pcr3-faq-q-text-closed { font-size: 14px; }

    .pcr3-bottom-contact    { padding: 40px 0 60px 0; }
    .pcr3-bottom-contact p  { font-size: 16px; }
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — MOBILE 480px
  ══════════════════════════════════════════ */
  @media (max-width: 480px) {
    .pcr3-breadcrumb        { padding: 30px 0 0 0; }
    .pcr3-breadcrumb-inner  { padding: 0 16px; gap: 14px; }
    .pcr3-bc-item           { font-size: 14px; }
    .pcr3-bc-sep            { width: 18px; height: 18px; margin: 0 4px; }
    .pcr3-inner             { padding: 0 16px; }
    .pcr3-divider           { padding: 0 16px; }

    .pcr3-intro             { padding: 30px 0 0px 0; }
    .pcr3-intro-grid        { gap: 30px; }
    .pcr3-intro-img         { height: 432px; }
    .pcr3-intro-title       { font-size: 20px; margin-bottom: 20px; }

    .pcr3-adv-section       { padding: 60px 0 60px 0; }
    .pcr3-section-red-title { font-size: 20px; margin-bottom: 0px;text-align:left; }
    .pcr3-sub-heading       { font-size: 20px; margin-bottom: 30px; }
    .pcr3-icon-row          { flex-direction: column; align-items: start; gap: 30px; margin-bottom: 30px; }
    .pcr3-icon-card         { flex: none; width: 100%; max-width: 100%; text-align: left; align-items: flex-start; }
    .pcr3-icon-circle img   { width: 60px; height: 60px; }
    .pcr3-icon-text         { font-size: 16px; }

    .pcr3-app-section       { padding: 0 0 60px 0; }
    .pcr3-contact-para      { font-size: 16px; margin-top: 30px; }
    .pcr3-contact-para1 { font-size: 16px; margin-bottom: 30px;}
    .pcr3-section-red-title1 { font-size: 20px; margin-bottom: 30px; }

    .pcr3-spec-section      { padding: 40px 0 0px 0; }
    .pcr3-spec-table-wrap   { overflow-x: auto; }
    .pcr3-spec-table        { min-width: auto; }
    .pcr3-spec-table thead th { padding: 30px 8px; font-size: 16px; }
    .pcr3-spec-table tbody td { padding: 30px 8px; font-size: 14px; }

    .pcr3-faq-section       { padding: 60px 0 60px 0; }
    .pcr3-faq-grid          { gap: 30px; }
    .pcr3-faq-title         { font-size: 20px; }
    .pcr3-faq-question      { padding: 24px 16px 20px 16px; }
    .pcr3-faq-answer        { padding: 0 16px 16px 16px; font-size: 13px; }

    .pcr3-bottom-contact    { padding: 60px 0 60px 0; }
    .pcr3-bottom-contact p  { font-size: 16px; text-align:left;}
  }
`;

/* Chevron SVGs */
const ChevronUp = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M5 12.5L10 7.5L15 12.5" stroke="#202020" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="#202020" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ── Smooth FAQ accordion item ── */
const FaqItem = ({ q, a, isOpen, onToggle }) => {
  const bodyRef = useRef(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + "px";
    } else {
      el.style.maxHeight = el.scrollHeight + "px";
      el.getBoundingClientRect();
      el.style.maxHeight = "0px";
    }
  }, [isOpen]);

  return (
    <div className={`pcr3-faq-item ${isOpen ? "pcr3-faq-item-open" : "pcr3-faq-item-closed"}`}>
      <button className="pcr3-faq-question" onClick={onToggle}>
        <span className={isOpen ? "pcr3-faq-q-text-open" : "pcr3-faq-q-text-closed"}>{q}</span>
        <span className="pcr3-faq-chevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
      </button>
      <div
        ref={bodyRef}
        className={`pcr3-faq-body${isOpen ? " pcr3-faq-body-open" : ""}`}
      >
        <p className="pcr3-faq-answer">{a}</p>
      </div>
    </div>
  );
};

export default function PreiconR3() {
  /* First FAQ open by default (index 0), accordion: only one open at a time */
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (i) => {
    setOpenIndex(prev => (prev === i ? null : i));
  };

  return (
    <div className="pcr3-wrap">
      <style>{CSS}</style>

      {/* ══ 1. BREADCRUMB ══ */}
      <nav className="pcr3-breadcrumb" aria-label="Breadcrumb">
        <div className="pcr3-breadcrumb-inner">
          <a href="/" className="pcr3-bc-item">Home</a>
          <span className="pcr3-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="#" className="pcr3-bc-item">Business</a>
          <span className="pcr3-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="/products" className="pcr3-bc-item">Clean Air</a>
          <span className="pcr3-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <span className="pcr3-bc-item pcr3-active">Precicon R3</span>
        </div>
      </nav>

      {/* ══ 2. INTRO ══ */}
      <section className="pcr3-intro">
        <div className="pcr3-inner">
          <div className="pcr3-intro-grid">

            {/* Left — product image */}
            <div className="pcr3-intro-img-wrap">
              <img className="pcr3-intro-img" src={ProductImg} alt="Precicon R3" />
            </div>

            {/* Right — title + paragraphs */}
            <div className="pcr3-intro-right">
              <h1 className="pcr3-intro-title">
                Precicon R3 – Advanced Thyristor-Based Controller For Electrostatic Precipitators
              </h1>
              <p className="pcr3-para">
                Ador Powertron's Precicon R3 is a next-generation thyristor-based power controller, offering 3-phase input for greater stability and efficiency compared to three-phase systems. This makes it ideal for large-scale Electrostatic Precipitators (ESPs) that require high precision in power delivery across industries like cement, steel, power, and chemicals. With IP-54-rated protection, it performs reliably in challenging environments while CAN Bus communication ensures easy system integration and smooth operation.
              </p>
              <p className="pcr3-para">
                What sets Precicon R3 apart is its ability to deliver consistent, interference-free power even under fluctuating load conditions, thanks to galvanic isolation and in-built rapping control for optimized dust removal. The 3-phase input reduces the risk of voltage dips and enhances dust separation efficiency. Additionally, remote monitoring through DCS integration with the DCSIM Gateway helps ensure continuous operation with minimal downtime.
              </p>
              <p className="pcr3-para">
                Choose Precicon R3 to maximize ESP efficiency, reduce energy losses, and achieve superior environmental compliance for your industrial processes.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ══ 3. ADVANTAGES ══ */}
      <section className="pcr3-adv-section">
        <div className="pcr3-inner">
          <h2 className="pcr3-section-red-title">Precicon R3 : Advantages &amp; Applications</h2>
          <h3 className="pcr3-sub-heading">Advantages</h3>

          {/* Row 1 — 3 cards */}
          <div className="pcr3-icon-row">
            {ADVANTAGES.slice(0, 3).map((item, i) => (
              <div key={i} className="pcr3-icon-card">
                <div className="pcr3-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr3-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Row 2 — 2 cards centered */}
          <div className="pcr3-icon-row">
            {ADVANTAGES.slice(3, 5).map((item, i) => (
              <div key={i} className="pcr3-icon-card">
                <div className="pcr3-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr3-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="pcr3-divider"><hr /></div>

      {/* ══ 4. END APPLICATIONS ══ */}
      <section className="pcr3-app-section">
        <div className="pcr3-inner">
          <h3 className="pcr3-sub-heading">End Applications</h3>

          {/* 2 cards centered */}
          <div className="pcr3-icon-row">
            {END_APPLICATIONS.map((item, i) => (
              <div key={i} className="pcr3-icon-card">
                <div className="pcr3-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr3-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="pcr3-contact-para1">
            Precicon R3 ensures peak ESP performance, energy efficiency, and reliable compliance with industrial standards.
          </p>
          <p className="pcr3-contact-para" >
            <a href="/contact">Contact us</a> today to learn how Precicon R3 can enhance your ESP's efficiency while meeting your environmental goals
          </p>
        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="pcr3-divider"><hr /></div>

      {/* ══ 5. PRODUCT SPECIFICATIONS ══ */}
      <section className="pcr3-spec-section">
        <div className="pcr3-inner">
          <h2 className="pcr3-section-red-title1">Product Specifications</h2>
          <div className="pcr3-spec-table-wrap">
            <table className="pcr3-spec-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Conventional</th>
                </tr>
              </thead>
              <tbody>
                {SPECS.map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    <td>
                      {row.isImage
                        ? <img className="pcr3-spec-hmi-img" src={row.value} alt="HMI" />
                        : row.value
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ══ 6. FAQ ══ */}
      <section className="pcr3-faq-section">
        <div className="pcr3-inner">
          <div className="pcr3-faq-grid">

            {/* Left — title */}
            <h2 className="pcr3-faq-title">Frequently Asked Questions (FAQs)</h2>

            {/* Right — accordion */}
            <div className="pcr3-faq-list">
              {FAQS.map((faq, i) => (
                <FaqItem
                  key={i}
                  q={faq.q}
                  a={faq.a}
                  isOpen={openIndex === i}
                  onToggle={() => handleToggle(i)}
                />
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="pcr3-divider"><hr /></div>

      {/* ══ BOTTOM CONTACT ══ */}
      <section className="pcr3-bottom-contact">
        <div className="pcr3-inner">
          <p>
            <a href="/contact">Contact us</a> today to discover how Precicon R3 can deliver efficiency, reliability, and compliance for your ESP systems.
          </p>
        </div>
      </section>

    </div>
  );
}